import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Crown, Users as UsersIcon, FileText, ChevronRight, RefreshCw } from "lucide-react";
import Header from "@/components/Header";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/customers", { params: { q: q || undefined, limit: 500 } });
      setCustomers(data.customers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load customers");
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="admin-customers-page">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-indigo-600 mb-2">Admin · Customer book</div>
            <h1 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900">Customers</h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              Every user account. Drill into a customer to view + manage their permits on their behalf. All actions
              are audit-stamped as "by {`<your email>`} on behalf of customer".
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="customers-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} />Reload
          </Button>
        </div>

        <div className="relative max-w-md mb-6">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search by email…" value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9 text-sm"
            data-testid="customers-search-input"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Permits</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-xs text-slate-500">Loading…</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-xs text-slate-500" data-testid="customers-empty">No customers match the current filter.</td></tr>
              ) : customers.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-none">
                        <User className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-mono text-xs text-slate-900 truncate">{c.email}</div>
                        {c.name ? <div className="text-[10px] text-slate-500 truncate">{c.name}</div> : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {c.is_pro && (
                        <Badge className="bg-amber-100 text-amber-900 border-amber-300 gap-1 text-[10px] font-mono">
                          <Crown className="w-3 h-3" />Pro · {c.pro_tier || ""}
                        </Badge>
                      )}
                      {c.role === "admin" && <Badge variant="outline" className="text-[10px] font-mono">admin</Badge>}
                      {c.city_partner && <Badge variant="outline" className="text-[10px] font-mono bg-indigo-50 border-indigo-200 text-indigo-700">city partner</Badge>}
                      {!c.is_pro && c.role !== "admin" && !c.city_partner && (
                        <span className="text-[10px] text-slate-400 font-mono">free / trial</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid={`customer-permit-count-${c.id}`}>
                    {c.permit_count > 0 ? (
                      <span className="font-semibold text-slate-900">{c.permit_count}</span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => navigate(`/admin/customers/${c.id}`)}
                      className="text-xs gap-1"
                      data-testid={`customer-open-${c.id}`}
                    >
                      Open <ChevronRight className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-xs text-slate-500 font-mono flex items-center gap-2">
          <UsersIcon className="w-3 h-3" />{customers.length} customer{customers.length === 1 ? "" : "s"}
        </div>
      </main>
    </div>
  );
}
